import { Checkbox, CheckboxProps, Radio, RadioProps } from "@chakra-ui/react";
import { ColumnHelper, DisplayColumnDef, RowData } from "@tanstack/react-table";
import {
  CheckboxInputRef,
  getCheckboxSelectableRowProps,
  SelectableRowProps,
  SelectableRowTableMeta,
} from "components/Shared/ChakraReactTable/useSelectableRow";
import { createTableMetaHelper } from "components/Shared/ChakraReactTable/utils";

export type CheckboxSelectionCellProps = Pick<
  SelectableRowProps,
  "isDisabled" | "isSelected" | "handleChange"
> &
  (CheckboxProps & RadioProps) & {
    isMulti?: boolean;
    inputRef?: CheckboxInputRef;
  };

export default function CheckboxSelectionCell({
  isDisabled,
  isMulti,
  isSelected,
  handleChange,
  inputRef,
  ...rest
}: CheckboxSelectionCellProps) {
  const Component = isMulti ? Checkbox : Radio;
  return (
    <Component
      ref={inputRef}
      inputProps={{
        onClick: (evt) => evt.stopPropagation(),
      }}
      onClick={(evt) => evt.stopPropagation()}
      isChecked={isSelected}
      onChange={handleChange}
      isDisabled={isDisabled}
      {...rest}
    />
  );
}

function makeSelectionCell<D, M extends SelectableRowTableMeta<D>>(
  columnHelper: ColumnHelper<D>,
  isMulti: boolean,
  columnDef?: Partial<DisplayColumnDef<D, unknown>>,
) {
  const metaFrom = createTableMetaHelper<D, M>();
  return columnHelper.display({
    id: "select",
    header: () => " ",
    cell: (cell) => {
      const meta = metaFrom.cell(cell);
      const checkboxProps = getCheckboxSelectableRowProps(cell.row, meta);
      return (
        <CheckboxSelectionCell
          {...checkboxProps}
          isMulti={isMulti}
          verticalAlign="baseline"
        />
      );
    },
    size: 30,
    ...columnDef,
    meta: {
      ...columnDef?.meta,
      props: {
        onClick: (event) => event.stopPropagation(),
        pl: 3,
        pr: 0,
        ...columnDef?.meta?.props,
      },
    },
  });
}

export function makeCheckboxSelectionCell<
  D,
  M extends SelectableRowTableMeta<D>,
>(
  columnHelper: ColumnHelper<D>,
  columnDef?: Partial<DisplayColumnDef<D, unknown>>,
) {
  return makeSelectionCell<D, M>(columnHelper, true, columnDef);
}

type SelectableRowTableMetaWithAll<D extends RowData> =
  SelectableRowTableMeta<D> & {
    isAllSelected: boolean;
    isIndeterminate: boolean;
    isSelectAllDisabled: boolean;
    onIsAllSelectedChange: (isAllSelected: boolean) => void;
  };

export function makeCheckboxSelectionCellWithAll<
  D,
  M extends SelectableRowTableMetaWithAll<D>,
>(
  columnHelper: ColumnHelper<D>,
  columnDef?: Partial<DisplayColumnDef<D, unknown>>,
) {
  const metaFrom = createTableMetaHelper<D, M>();
  return makeCheckboxSelectionCell(columnHelper, {
    ...columnDef,
    header: (cell) => {
      const meta = metaFrom.cell(cell);
      return (
        <Checkbox
          isChecked={meta.isAllSelected}
          isDisabled={meta.isSelectAllDisabled}
          isIndeterminate={meta.isIndeterminate}
          onChange={() => meta.onIsAllSelectedChange(!meta.isAllSelected)}
        />
      );
    },
  });
}
